import {assert} from 'Asserts';
import * as SpreadsheetBulkImportConstants from 'bulk-import/SpreadsheetBulkImportConstants';
import {ColumnDefinitionForSpreadsheetImport} from 'notebook/structured-tables/spreadsheet-import/ColumnDefinitionForSpreadsheetImport';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {toStringTypedValueCell} from 'notebook/tables/typed-values/DataTypes';

export interface ImportedStructuredTableRow {
  nonSchemaFieldCellsByColumnType: {[columnType: string]: string};
  /**
   * Field ID is the numeric dbId, not the API ID
   */
  schemaFieldCellsByFieldId: {[schemaFieldId: number]: string};
}

function isSpreadsheetColumnTypeForSchemaField(
  spreadsheetColumnType: SpreadsheetBulkImportConstants.COLUMN_TYPE
): boolean {
  return (
    spreadsheetColumnType === SpreadsheetBulkImportConstants.COLUMN_TYPE.SCHEMA_FIELD ||
    spreadsheetColumnType === SpreadsheetBulkImportConstants.COLUMN_TYPE.CONTAINER_SCHEMA_FIELD ||
    spreadsheetColumnType === SpreadsheetBulkImportConstants.COLUMN_TYPE.RESULT_SCHEMA_FIELD ||
    spreadsheetColumnType === SpreadsheetBulkImportConstants.COLUMN_TYPE.PROCESS_TASK_SCHEMA_FIELD ||
    spreadsheetColumnType === SpreadsheetBulkImportConstants.COLUMN_TYPE.PROCESS_OUTPUT_SCHEMA_FIELD ||
    spreadsheetColumnType === SpreadsheetBulkImportConstants.COLUMN_TYPE.PLATE_SCHEMA_FIELD ||
    spreadsheetColumnType === SpreadsheetBulkImportConstants.COLUMN_TYPE.BOX_SCHEMA_FIELD
  );
}

function getImportedTextValue(
  importedStructuredTableRow: ImportedStructuredTableRow,
  columnDefinitionForSpreadsheetImport: ColumnDefinitionForSpreadsheetImport
): string | null {
  const {spreadsheetColumnType, columnDefinition} = columnDefinitionForSpreadsheetImport;

  if (spreadsheetColumnType == null) {
    return null;
  }

  if (columnDefinition.type === TableColumnType.TAG_VALUE) {
    assert(
      isSpreadsheetColumnTypeForSchemaField(spreadsheetColumnType),
      'Unexpected column type mismatch between spreadsheet and structured table',
      {
        columnDefinitionType: columnDefinition.type,
        spreadsheetColumnType,
      }
    );

    // This function is not used for rendering, but for editing the table, so its ok to access live schemaField
    return importedStructuredTableRow.schemaFieldCellsByFieldId[columnDefinition.schemaField.id];
  } else {
    return importedStructuredTableRow.nonSchemaFieldCellsByColumnType[spreadsheetColumnType];
  }
}

/**
 * This function takes the results of a spreadsheet import and prepares
 * them for insertion into the table. The output is a 2D matrix of cells.
 */
export default function prepareImportedRows(
  importedRows: Array<ImportedStructuredTableRow>,
  columnDefinitionsForImport: Array<ColumnDefinitionForSpreadsheetImport>
): Array<Array<Cell>> {
  return importedRows.map(
    (importedStructuredTableRow): Array<Cell> =>
      columnDefinitionsForImport.map((columnDefinitionForSpreadsheetImport): Cell => {
        const textValue =
          getImportedTextValue(importedStructuredTableRow, columnDefinitionForSpreadsheetImport) ?? '';

        // If the imported text value is empty, return an empty cell so that isCellEmpty(cell) is true.
        // Downstream, callers use the column's default value if the imported cell is empty.
        if (!textValue) {
          return TableUtil.makeEmptyCell();
        }

        // Formulas should be stored in the cell's text field, not as a TypedValue
        if (TableUtil.isValidFormula(textValue)) {
          return TableUtil.makeCellWithText(textValue);
        }

        // Returning a StringTypedValue lets string resolution occur normally during evaluation.
        return toStringTypedValueCell(textValue);
      })
  );
}
